import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useDeletePerson, usePerson, usePersonTasks } from '../hooks/usePeople';
import { usePermissions } from '../hooks/usePermissions';
import { initials } from '../components/people/UtilizationBar';
import { PersonOverviewTab } from '../components/people/detail/PersonOverviewTab';
import { PersonEmploymentTab } from '../components/people/detail/PersonEmploymentTab';
import { PersonTasksTab } from '../components/people/detail/PersonTasksTab';
import { PersonCalendarTab } from '../components/people/detail/PersonCalendarTab';
import { PersonLeaveTab } from '../components/people/detail/PersonLeaveTab';
import { PersonResourcesTab } from '../components/people/detail/PersonResourcesTab';
import { PersonInvoicesTab } from '../components/people/detail/PersonInvoicesTab';
import { PersonActivityTab } from '../components/people/detail/PersonActivityTab';
import { PersonIntegrationsTab } from '../components/people/detail/PersonIntegrationsTab';
import { MeetingNotesTab } from '../components/meetingNotes/MeetingNotesTab';
import { FadeIn, Skeleton, SkeletonList, SkeletonTable } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useAuthStore } from '../store/useAuthStore';

// Uniform tab layout matching ProjectDetailPage and ClientDetailPage (CLE-237).
const TABS = [
  'overview',
  'employment',
  'tasks',
  'calendar',
  'leave',
  'invoices',
  'documents',
  'notes',
  'integrations',
  'activity',
] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Prezentare generală',
  employment: 'Angajare',
  tasks: 'Sarcini',
  calendar: 'Calendar',
  leave: 'Concedii',
  invoices: 'Facturi',
  documents: 'Documente',
  notes: 'Notițe ședință',
  integrations: 'Integrări',
  activity: 'Activitate',
};

export function PersonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: person, isLoading, isError } = usePerson(id);
  const { data: workload, isLoading: workloadLoading, isError: workloadError } = usePersonTasks(id);
  const deleteMutation = useDeletePerson();
  const confirm = useConfirm();
  // The Angajare tab exposes compensation data (IBAN, payroll), so it is only
  // visible with the compensation-visibility flag (ADMIN bypasses).
  const { can } = usePermissions();
  const canViewEmployment = can('people', 'canViewRates');
  // Deleting a member disables their login account, so gate it on the same
  // privilege as account provisioning (matches the backend route).
  const canDelete = can('people', 'canManageRoles');
  // The Concedii tab is one person's leave: your own is always yours to read,
  // someone else's needs the team-wide flag.
  const signedInUserId = useAuthStore((s) => s.user?.id);
  const isSelf = Boolean(person?.userId && person.userId === signedInUserId);
  const canViewLeave = isSelf || can('leave', 'canViewAll');
  const visibleTabs = TABS.filter(
    (t) => (t !== 'employment' || canViewEmployment) && (t !== 'leave' || canViewLeave),
  );

  const initialTab = (window.location.hash.replace('#', '') as Tab) || 'overview';
  const [tab, setTab] = useState<Tab>(TABS.includes(initialTab) ? initialTab : 'overview');

  function selectTab(next: Tab) {
    setTab(next);
    window.location.hash = next;
  }

  async function handleDelete() {
    if (!id) return;
    if (
      !(await confirm({
        message: 'Ștergi acest membru și dezactivezi contul asociat?',
        danger: true,
      }))
    )
      return;
    await deleteMutation.mutateAsync(id);
    void navigate('/people');
  }

  if (isLoading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="flex items-center gap-4">
          <Skeleton circle width={64} height={64} />
          <div className="space-y-2">
            <Skeleton className="h-7" width={220} />
            <Skeleton className="h-4" width={160} />
          </div>
        </div>
        <div className="mt-8 space-y-3">
          <Skeleton className="h-5" width={140} />
          <SkeletonTable rows={4} columns={6} />
        </div>
        <div className="mt-8 space-y-3">
          <Skeleton className="h-5" width={120} />
          <SkeletonList rows={4} badge />
        </div>
      </div>
    );
  }

  if (isError || !person) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-red-600">Persoana nu a fost găsită.</p>
        <Link to="/people" className="mt-4 inline-block text-sm text-blue-600 underline">
          Înapoi la director
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <Link
        to="/people"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft size={14} /> Echipă
      </Link>

      {/* Header — same pattern as Client/Project detail. */}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          {person.avatarUrl ? (
            <img
              src={person.avatarUrl}
              alt={person.fullName}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-lg font-semibold text-white">
              {initials(person.fullName)}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{person.fullName}</h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
              {person.title && <span>{person.title}</span>}
              {person.department && <span>· {person.department}</span>}
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                  person.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {person.isActive ? 'Activ' : 'Inactiv'}
              </span>
            </div>
          </div>
        </div>
        {canDelete && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={15} /> Șterge
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6 overflow-x-auto">
          {visibleTabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => selectTab(t)}
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                tab === t
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        <FadeIn>
          {tab === 'overview' && (
            <PersonOverviewTab
              person={person}
              workload={workload}
              workloadLoading={workloadLoading}
            />
          )}
          {tab === 'employment' && canViewEmployment && (
            <PersonEmploymentTab personId={person.id} />
          )}
          {tab === 'tasks' && (
            <PersonTasksTab
              workload={workload}
              workloadLoading={workloadLoading}
              workloadError={workloadError}
              personName={person.fullName}
            />
          )}
          {tab === 'calendar' && <PersonCalendarTab userId={person.userId} />}
          {tab === 'leave' && canViewLeave && (
            <PersonLeaveTab personId={person.id} userId={person.userId} />
          )}
          {tab === 'invoices' && <PersonInvoicesTab personId={person.id} userId={person.userId} />}
          {tab === 'documents' && <PersonResourcesTab personId={person.id} />}
          {tab === 'notes' && <MeetingNotesTab owner={{ personId: person.id }} variant="tab" />}
          {tab === 'integrations' && <PersonIntegrationsTab userId={person.userId} />}
          {tab === 'activity' && <PersonActivityTab personId={person.id} />}
        </FadeIn>
      </div>
    </div>
  );
}
